import { Fragment, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { FatArrowDown, Info, ListRow, Remove } from 'basic-components';

import { creatorSeriesActions } from 'actions/series';

import SymbolColumns from './SymbolColumns';

import { Else, If, Then, When } from 'react-if';

import classNames from 'classnames';

const Row = ({ symbols, metadata, groupName, pattern, query, scrollRef, columns, value, index, swapIsInfoModalOpen, setSelectedSymbolIndex, isPattern }) => {
  const dispatch = useDispatch(),
        removeItem = useCallback(() => dispatch(creatorSeriesActions.swapSelectedSymbol({ symbols, metadata, groupName, pattern, query })), [dispatch, groupName, metadata, pattern, symbols, query]),
        symbolValue = useMemo(() => {
          if(symbols?.Symbol) {
            return symbols.Symbol
          }
          const entries = Object.entries(symbols || {})
          if (entries.length === 0) {
            return ""
          }
          const [firstKey, firstValue] = entries[0]
          return `${firstKey}: ${firstValue}`
        }, [symbols]),
        areColumnCustom = useMemo(() => columns.length > 0, [columns]),
        [areColumnsOpen, setAreColumnsOpen] = useState(false),
        swapAreColumnsOpen = useCallback(() => setAreColumnsOpen(areOpen => !areOpen), []),
        openModal = useCallback(() => {
          setSelectedSymbolIndex(index)
          swapIsInfoModalOpen()
        }, [index, setSelectedSymbolIndex, swapIsInfoModalOpen]),
        metadataValues = useMemo(() => Object.values(metadata), [metadata]),
        metadataValuesLength = useMemo(() => {
          let toReturn = metadataValues?.length
          if(groupName) {
            toReturn += 1
          }
          if(query) {
            toReturn += 1
          }
          return toReturn
        }, [groupName, metadataValues, query]),
        handleOnInfoKeyDown = useCallback(e => {
          if ((e.key === "Enter" || e.key === " ")) {
            e.preventDefault()
            openModal()
          }
        }, [openModal]),
        handleOnRmoveKeyDown = useCallback(e => {
          if ((e.key === "Enter" || e.key === " ")) {
            e.preventDefault()
            removeItem()
          }
        }, [removeItem]),
        handleOnColumnsKeyDown = useCallback(e => {
          if ((e.key === "Enter" || e.key === " ")) {
            e.preventDefault()
            swapAreColumnsOpen()
          }
        }, [swapAreColumnsOpen])

  return (
    <Fragment>
      <ListRow
          className={classNames(
            "ng-office-app__authed__content__basket__body__data__basket__items__item",
            { "ng-office-app__authed__content__basket__body__data__basket__items__item--with-counter": metadataValuesLength > 1 }
          )}
          offset={8000}
          scrollContRef={scrollRef}
      >
        <div 
            className="ng-office-app__authed__content__basket__body__data__basket__items__item__info"
            onClick={openModal}
            onKeyDown={handleOnInfoKeyDown}
            tabIndex={0}
        >
          <Info
              color="blue-bright"
              height={16}
              width={16}
          />
        </div>
        <If condition={isPattern}>
          <Then>
            {() => (
              <Fragment>
                <div className="ng-office-app__authed__content__basket__body__data__basket__items__item__cell">
                  {metadataValues?.length > 0 ? String(metadataValues[0]) : (groupName || query)}
                </div>
                <When condition={metadataValuesLength > 1}>
                    {() => (
                      <div className="ng-office-app__authed__content__basket__body__data__basket__items__item__cell ng-office-app__authed__content__basket__body__data__basket__items__item__cell--subcell">
                        {`${metadataValuesLength - 1} more...`}
                      </div>
                    )}
                </When>
              </Fragment>
            )}
          </Then>
          <Else>
            {() => (
              <Fragment>
                <div className="ng-office-app__authed__content__basket__body__data__basket__items__item__cell">
                  {groupName}
                </div>
                <div className="ng-office-app__authed__content__basket__body__data__basket__items__item__cell">
                  {symbolValue}
                </div>
              </Fragment>
            )}
          </Else>
        </If>
        <div 
            className="ng-office-app__authed__content__basket__body__data__basket__items__item__columns"
            onClick={swapAreColumnsOpen}
            onKeyDown={handleOnColumnsKeyDown}
            tabIndex={0}
        >
          <div className="ng-office-app__authed__content__basket__body__data__basket__items__item__columns__label">
            {areColumnCustom ? "Custom" : "Default"}
          </div>
          <FatArrowDown
              color="black-base"
              height={12}
              width={12}
          />
        </div>
        <div 
            className="ng-office-app__authed__content__basket__body__data__basket__items__item__action"
            onClick={removeItem}
            onKeyDown={handleOnRmoveKeyDown}
            tabIndex={0}
        >
          <Remove
              color="red-dark"
              height={16}
              width={16}
          />
        </div>
      </ListRow>
      <When condition={areColumnsOpen}>
        {() => (
          <SymbolColumns 
              groupName={groupName}
              index={index}
              metadata={metadata}
              onClose={swapAreColumnsOpen}
              pattern={pattern}
              symbolColumns={columns}
              symbols={symbols}
              value={value}
          />
        )}
      </When>
    </Fragment>
  )
}

Row.defaultProps = {
  columns: [],
  groupName: undefined,
  isPattern: false,
  metadata: {},
  pattern: "true",
  query: undefined,
  scrollRef: {},
  symbols: {},
  value: undefined
}

Row.propTypes = {
  columns: PropTypes.array, 
  groupName: PropTypes.string, 
  index: PropTypes.number.isRequired,
  isPattern: PropTypes.bool,
  metadata: PropTypes.object, 
  pattern: PropTypes.string,
  query: PropTypes.string,
  scrollRef: PropTypes.any,
  setSelectedSymbolIndex: PropTypes.func.isRequired,
  swapIsInfoModalOpen: PropTypes.func.isRequired,
  symbols: PropTypes.object,
  value: PropTypes.any,
}

export default Row